/**
 * Tracked outbound deal links.
 *
 * Canon § 11 ranks the weekly and monthly Top lists by *qualified buyer outbound clicks*,
 * and a call to action pointing straight at `deal.dealUrl` produces no such record. Core
 * owns the tracked endpoint (`contracts/API.md` § Tracked outbound click shape); this
 * module is the single place in the bundle that names it, so no surface can invent its own
 * URL shape or forget the label that says where a click came from.
 *
 * What this file deliberately does **not** do: count, filter, deduplicate or qualify
 * anything. Whether a click is a real buyer or a prefetch is core's decision (canon § 14
 * keeps the lists unbuyable, and a browser cannot be trusted to judge itself). The bundle's
 * whole responsibility is to send the buyer through the endpoint that can decide.
 */
import { apiUrl } from "../config/env";
import { safeExternalUrl } from "./url";

/**
 * Core's `surface` allow-list (`contracts/API.md`, mirrored from `click_events.surface`).
 *
 * It is an allow-list rather than free text so the answer to "which surface produces
 * clicks" cannot be quietly wrong. Core degrades an unknown value to `other` rather than
 * refusing the navigation, but this repo never relies on that: a caller names its surface
 * and the type stops a typo at build time.
 *
 * `founding_wall` is the one surface with a **ranking** consequence, and it is core's to
 * enforce: canon decision `D-018` excludes it from canon § 11's weekly and monthly lists at
 * the ranking query. Nothing in this bundle filters or weights anything — a browser cannot be
 * trusted to judge its own clicks — but sending the right label is what lets core exclude
 * them. A wall link that named `daily_board`, or fell through to `other`, would put ten deals
 * back into the ranking `AGENTS.md` § 14 says money must not be able to buy.
 */
export const CLICK_SURFACES = [
  "daily_board",
  "honorable",
  "weekly",
  "monthly",
  "deal_detail",
  "founding_wall",
  "other",
] as const;

export type ClickSurface = (typeof CLICK_SURFACES)[number];

/**
 * The tracked URL for one deal's call to action, or `null` when there must be no link.
 *
 * `dealUrl` is passed in and re-checked even though the browser never navigates to it
 * directly. That is the point: `safeExternalUrl` still decides whether a call to action
 * exists at all (canon `docs/SECURITY.md` § URLs). A deal whose destination is not a safe
 * `http`/`https` URL must render with **no** action rather than with a tracked link to a
 * destination core is going to refuse — a tracked link to a `404` is a worse buyer
 * experience than an honest "unavailable", and it would manufacture a click record for a
 * visit that can never land.
 *
 * The URL is built from the configured API base (`src/config/env.ts`), never hard-coded per
 * environment, so a staging bundle records staging clicks.
 */
export function trackedDealUrl(
  dealId: string,
  surface: ClickSurface,
  dealUrl: string | null | undefined,
): string | null {
  if (safeExternalUrl(dealUrl) === null) return null;
  return apiUrl(clickPath(dealId, surface));
}

/**
 * The base-relative half of the tracked URL, e.g.
 * `/v1/deals/deal-1/click?surface=weekly`.
 *
 * Exported so the environment test can compose it against each environment's configured
 * base (`.env.development`, `.env.staging`, `.env.production`) and prove the same bundle
 * code produces the right absolute URL in all three. Nothing in `src/` should call this
 * directly — a component that built its own absolute URL would be the hard-coded origin
 * this module exists to prevent.
 *
 * `surface` is a member of a literal union and needs no escaping, but it is encoded anyway:
 * the guarantee should hold at runtime for a value that arrived from untyped JavaScript,
 * not only where TypeScript was looking.
 */
export function clickPath(dealId: string, surface: ClickSurface): string {
  return `/v1/deals/${encodeURIComponent(dealId)}/click?surface=${encodeURIComponent(surface)}`;
}
